import React, { useEffect, useState, useCallback } from 'react';
import { api, type BlockDetail } from '../lib/api';
import { fmtDate, fmtClock } from '../lib/format';
import { OperaWindow, OperaSection, OperaField, type SideButton } from './OperaWindow';

interface Props {
  blockId: string;
  onClose: () => void;
  onChanged: () => void;
}

type Mode = 'view' | 'release-confirm';

function blockTypeLabel(t: string): string {
  switch (t) {
    case 'MAINTENANCE':  return 'Maintenance';
    case 'DEEP_CLEAN':   return 'Deep Clean';
    case 'HOLD':         return 'Hold';
    case 'OUT_OF_ORDER': return 'Out of Order';
    case 'PRIVATE':      return 'Private (Owner)';
    default:             return t;
  }
}

export function BlockDetailModal({ blockId, onClose, onChanged }: Props) {
  const [data, setData] = useState<BlockDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>('view');
  const [releaseReason, setReleaseReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await api.blockDetail(blockId);
      setData(d);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [blockId]);

  useEffect(() => { load(); }, [load]);

  async function handleRelease() {
    setBusy(true);
    setError(null);
    try {
      await api.releaseBlock(blockId, releaseReason.trim());
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  if (loading || !data) {
    return (
      <OperaWindow title="BLOCK" onClose={onClose} size="compact"
        sideButtons={[{ label: 'Close', onClick: onClose }]}>
        <div className="loading">{error ?? 'Loading…'}</div>
      </OperaWindow>
    );
  }

  const bedDesc = data.room.displayName
    ? `Room ${data.room.number} (${data.room.displayName}) — ${data.bed.label}`
    : `Room ${data.room.number} — ${data.bed.label}`;

  const isPrivate = data.blockType === 'PRIVATE';
  const isActive = data.isActive;

  let sideButtons: SideButton[];
  if (mode === 'release-confirm') {
    sideButtons = [
      { label: 'Confirm', onClick: handleRelease, danger: true, disabled: busy },
      { label: 'Back',    onClick: () => { setMode('view'); setReleaseReason(''); }, disabled: busy },
      { label: 'Close',   onClick: onClose },
    ];
  } else {
    sideButtons = [
      isActive && !isPrivate
        ? { label: 'Release Block', onClick: () => setMode('release-confirm'), primary: true }
        : null,
      { label: 'Close', onClick: onClose },
    ].filter(Boolean) as SideButton[];
  }

  return (
    <OperaWindow
      title="BLOCK"
      status={isActive ? 'ACTIVE' : 'RELEASED'}
      recordId={data.id.slice(-8)}
      onClose={onClose}
      size="compact"
      sideButtons={sideButtons}
    >
      {error && <div className="modal-error">{error}</div>}

      {mode === 'view' && (
        <>
          <OperaSection title="Block">
            <OperaField label="Bed">
              <span className="opera-readonly-strong">{bedDesc}</span>
            </OperaField>
            <OperaField label="Type">
              <span>{blockTypeLabel(data.blockType)}</span>
            </OperaField>
            <OperaField label="Reason">
              <span>{data.reason}</span>
            </OperaField>
            <OperaField label="From">
              <span>{fmtDate(data.startsAt)}</span>
            </OperaField>
            <OperaField label="Until">
              <span>{fmtDate(data.endsAt)}</span>
            </OperaField>
          </OperaSection>

          {!isActive && (
            <OperaSection title="Released">
              {data.releasedAt && (
                <OperaField label="Released at">
                  <span>{fmtClock(data.releasedAt)}</span>
                </OperaField>
              )}
              {data.releasedBy && (
                <OperaField label="By">
                  <span>{data.releasedBy}</span>
                </OperaField>
              )}
              {data.releasedReason && (
                <OperaField label="Reason">
                  <span>{data.releasedReason}</span>
                </OperaField>
              )}
            </OperaSection>
          )}

          <div className="opera-audit">
            Created {fmtClock(data.createdAt)} by {data.createdBy}
          </div>

          {isPrivate && (
            <div style={{ fontSize: 11, color: '#791F1F', marginTop: 8 }}>
              🔒 PRIVATE blocks cannot be released through this interface.
            </div>
          )}
        </>
      )}

      {mode === 'release-confirm' && (
        <OperaSection title="Release Block">
          <p style={{ margin: '4px 0 10px', fontSize: 12 }}>
            Release the block on <strong>{bedDesc}</strong>?
            <br /><br />
            The bed will become available for new bookings and the room will be
            flagged <strong>DIRTY</strong> for housekeeping to verify.
          </p>
          <OperaField label="Reason">
            <input
              type="text"
              value={releaseReason}
              onChange={e => setReleaseReason(e.target.value)}
              placeholder="optional — e.g. work completed, false alarm"
            />
          </OperaField>
        </OperaSection>
      )}
    </OperaWindow>
  );
}
